import {
  Body1,
  Body2,
  Button,
  Card,
  Checkbox,
  Container,
  Grid,
  GridItem,
  Link,
  Select,
  Table,
  TableBody,
  TableCell,
  TableRow,
} from '@sainsburys-tech/fable'
import { Close, SparkFilled } from '@sainsburys-tech/icons'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import { isFeatureEnabled } from '../../../common/api/featureApi'
import { useApiClient } from '../../../common/AppContext/appContext'
import ErrorRefresh from '../../../common/components/ErrorRefresh'
import ProductionPlanningButton from '../../../common/components/ProductionPlanningButton'
import ProgressLoading from '../../../common/components/ProgressLoading'
import { ErrorMessage } from '../../../common/enums/ErrorMessage'
import { Feature } from '../../../common/enums/FeatureEnum'
import useErrorHandler from '../../../common/hooks/useErrorHandler'
import { toUkTimeDateStringFrom } from '../../../common/utils/times'
import { checkStore, getUserStore } from '../../../utils/localStore'
import {
  getForecastReasons,
  getMixSkus,
  updateMixSkus,
  updateMixStatus,
} from '../../api/mixApi'
import { MixStageEnum } from '../../enums/MixStageEnum'
import { IForecastReason } from '../../types/IForecastReason'
import { IMix } from '../../types/IMix'
import IMixSku from '../../types/IMixSku'
import IMixSkuUpdate from '../../types/IMixSkuUpdate'
import MixSkusIncrementor from './MixSkusIncrementor'
import MixSkusRowCompleted from './MixSkusRowCompleted'
import MixSkusRowPending from './MixSkusRowPending'
import MixSkusTableHeader from './MixSkusTableHeader'
import MixSkusTitle from './MixSkusTitle'

const MixSkusView = () => {
  const params = useParams()
  const navigate = useNavigate()
  const currentMixId = Number(params.id)
  const apiClient = useApiClient()

  const planDate = useMemo(() => {
    return new Date(params.date ?? '')
  }, [params.date])

  const store = useMemo(() => getUserStore(), [])

  const [errorMessage, setErrorMessage] = useState('')
  const [currentMixScrollPosition, setCurrentMixScrollPosition] = useState(0)
  const [mix, setMix] = useState<IMix>()
  const [allMixSkusMarked, setAllMixSkusMarked] = useState(false)
  const [forecastReasons, setForecastReasons] = useState<IForecastReason[]>([])

  const [isLoading, setIsLoading] = useState(false)
  const [isNewAlgorithm, setIsNewAlgorithm] = useState(false)

  const { handleError, resetError } = useErrorHandler(setErrorMessage)

  const isMixCompleted = useCallback(() => {
    return (
      mix !== undefined &&
      mix.mixStatusStageId === (MixStageEnum.Completed as number)
    )
  }, [mix])

  const canProgressMix = useCallback(() => {
    return !isMixCompleted() && mix !== undefined && mix.isReadonly === false
  }, [isMixCompleted, mix])

  const openQpig = useCallback(
    (mixSku: IMixSku) => {
      const scrollAreaElement = document.getElementById('mix-scroll-area')
      if (scrollAreaElement) {
        setCurrentMixScrollPosition(scrollAreaElement.scrollTop)
      }
      navigate(`/qpig/${mixSku.skuId}`)
    },
    [navigate],
  )

  const skuSort = useCallback(
    (skuA: IMixSku, skuB: IMixSku): number =>
      skuA.positionOnOutput < skuB.positionOnOutput ? -1 : 1,
    [],
  )

  const getUpdatedMixSkus = useCallback(
    () =>
      mix?.mixSkus
        .filter((ms: IMixSku) => ms.changed === true)
        .map(
          (ms: IMixSku): IMixSkuUpdate =>
            ({
              marked: ms.marked,
              mixSkuId: ms.mixSkuId,
              productionActual: ms.productionActual,
              forecastReasonId: ms.forecastReasonId,
            }) as IMixSkuUpdate,
        ) || [],
    [mix],
  )

  const progressMix = useCallback(async () => {
    if (mix !== null && mix !== undefined) {
      const nextStage = mix.mixStatusStageId + 1
      const mixSkusToUpdate = getUpdatedMixSkus()

      try {
        await updateMixStatus(
          apiClient,
          mix.planId,
          mix.mixId,
          nextStage,
          mixSkusToUpdate,
          mix.rowVersion,
        )

        navigate('/bakeryview')
      } catch (e) {
        handleError(ErrorMessage.ProductionView.FailedToUpdate, e as Error)
      }
    }
  }, [mix, getUpdatedMixSkus, apiClient, navigate, handleError])

  const handleUpdateCloseView = useCallback(() => {
    if (!mix) {
      handleError('No Mix found')
      return
    }

    const mixSkusToUpdate = getUpdatedMixSkus()
    if (mixSkusToUpdate.length > 0) {
      updateMixSkus(apiClient, mix.mixId, mixSkusToUpdate)
        .then((response) => {
          if (!response.ok) {
            return
          }
        })
        .catch((e) => {
          handleError(ErrorMessage.ProductionView.FailedToUpdate, e as Error)
          return
        })
    }

    navigate('/bakeryview')
  }, [mix, getUpdatedMixSkus, apiClient, navigate, handleError])

  const handleSkuChecked = useCallback(
    (checked: boolean, event?: React.ChangeEvent<HTMLInputElement>) => {
      if (!mix || !event) return
      const mixSkuId = Number(event.target.value)
      const updatedMixSkus = mix.mixSkus.map((cur: IMixSku) =>
        cur.mixSkuId === mixSkuId
          ? { ...cur, marked: checked, changed: true }
          : cur,
      )

      setMix({ ...mix, mixSkus: updatedMixSkus })
    },
    [mix],
  )

  const handleAllSkusChecked = useCallback(
    (checked: boolean) => {
      if (!mix) return
      const updatedMixSkus = mix.mixSkus.map((cur: IMixSku) => ({
        ...cur,
        marked: checked,
        changed: true,
      }))
      setMix({ ...mix, mixSkus: updatedMixSkus })
      setAllMixSkusMarked(checked)
    },
    [mix],
  )

  const handleUpdateMixProductionActual = useCallback(
    (mixSkuId: number, isIncrement: boolean, productionActual?: number) => {
      if (!mix) {
        return
      }

      const updatedSkus = mix.mixSkus.map((mixSku) => {
        if (mixSku.mixSkuId === mixSkuId) {
          if (productionActual === null || productionActual === undefined) {
            mixSku.productionActual = isIncrement
              ? mixSku.productionActual + 1
              : mixSku.productionActual - 1
          } else {
            mixSku.productionActual = productionActual || 0
          }
          mixSku.changed = true
        }
        return mixSku
      })
      setMix({ ...mix, mixSkus: updatedSkus })
    },
    [mix],
  )

  const handleUpdateMixForecastReason = useCallback(
    (mixSkuId: number, forecastReasonId?: string) => {
      if (!mix) {
        return
      }

      const updatedSkus = mix.mixSkus.map((mixSku) => {
        if (mixSku.mixSkuId === mixSkuId) {
          mixSku.forecastReasonId = forecastReasonId
            ? parseInt(forecastReasonId, 10)
            : null
          mixSku.changed = true
        }
        return mixSku
      })
      setMix({ ...mix, mixSkus: updatedSkus })
    },
    [mix],
  )

  const renderSkuCheckbox = useCallback(
    (mixId: number, mixSku: IMixSku) => (
      <div>
        <Checkbox
          isStandalone={true}
          label={mixId.toString()}
          value={mixSku.mixSkuId.toString()}
          isChecked={!!mixSku.marked}
          onChange={handleSkuChecked}
          testid='mix-sku-checkbox'
        />
      </div>
    ),
    [handleSkuChecked],
  )

  const renderSkuQpigLink = useCallback(
    (mixSku: IMixSku) => (
      <Link
        className='c-common-hover-over-cursor c-link-black'
        onClick={() => openQpig(mixSku)}
        testid='mix-link'
      >
        <Body2>
          <span
            style={{
              display: 'flex',
              alignItems: 'center',
              width: '100%',
            }}
          >
            <span>{mixSku.skuName}</span>
            {mixSku.isCredibleProduct === true && (
              <SparkFilled className='c-star__icon' />
            )}
          </span>
        </Body2>
      </Link>
    ),
    [openQpig],
  )

  const renderSkuActualProductionIncrementor = useCallback(
    (mixSku: IMixSku) => (
      <MixSkusIncrementor
        mixSku={mixSku}
        setActualProduction={handleUpdateMixProductionActual}
      />
    ),
    [handleUpdateMixProductionActual],
  )

  const renderEditableSkuList = useCallback(
    (mx: IMix) =>
      mx?.mixSkus?.sort(skuSort).map((mixSku: IMixSku) => (
        <TableRow key={mixSku.mixSkuId} id={`MixSkuId_${mixSku.mixSkuId}`}>
          <TableCell>{renderSkuCheckbox(mx.mixId, mixSku)}</TableCell>
          <TableCell>{renderSkuQpigLink(mixSku)}</TableCell>
          <TableCell>
            <Body2>{mixSku.productionTarget}</Body2>
          </TableCell>
          <TableCell>{renderSkuActualProductionIncrementor(mixSku)}</TableCell>
          {isNewAlgorithm && (
            <TableCell>
              <Select
                label={' '}
                name='forecast reason'
                placeholder='Select a reason'
                options={forecastReasons?.map((reason) => ({
                  label: reason.description,
                  value: reason.forecastReasonId.toString(),
                }))}
                value={
                  mixSku.forecastReasonId
                    ? mixSku.forecastReasonId.toString()
                    : ''
                }
                onChange={(value: string) =>
                  handleUpdateMixForecastReason(mixSku.mixSkuId, value)
                }
                style={{ minWidth: 220 }}
              />
            </TableCell>
          )}
        </TableRow>
      )),
    [
      forecastReasons,
      handleUpdateMixForecastReason,
      isNewAlgorithm,
      renderSkuActualProductionIncrementor,
      renderSkuCheckbox,
      renderSkuQpigLink,
      skuSort,
    ],
  )

  const renderSkuList = useCallback(
    (mx: IMix) => {
      if (mx.mixStatusStageId === (MixStageEnum.InProgress as number)) {
        return renderEditableSkuList(mx)
      }

      if (mx.mixStatusStageId === (MixStageEnum.Completed as number)) {
        return mx.mixSkus
          .sort(skuSort)
          .map((mixSku) => (
            <MixSkusRowCompleted
              key={mixSku.mixSkuId}
              mixSku={mixSku}
              openQpig={openQpig}
              forecastReasons={forecastReasons}
            />
          ))
      }

      return mx.mixSkus
        .sort(skuSort)
        .map((mixSku) => (
          <MixSkusRowPending
            key={mixSku.mixSkuId}
            mixSku={mixSku}
            openQpig={openQpig}
          />
        ))
    },
    [forecastReasons, openQpig, renderEditableSkuList, skuSort],
  )

  const renderEditButton = useCallback(
    (mx: IMix) => {
      if (mx.isCompletedEditable === true) {
        return (
          <ProductionPlanningButton
            isFullWidth={true}
            handler={() => progressMix()}
            variant='secondary'
            testid='mix-edit-button'
          >
            Edit
          </ProductionPlanningButton>
        )
      } else {
        return (
          <Button
            disabled={mx.updating === true}
            isFullWidth
            onClick={handleUpdateCloseView}
            testid='mix-close-button'
          >
            Close
          </Button>
        )
      }
    },
    [progressMix, handleUpdateCloseView],
  )

  useEffect(() => {
    const initialLoad = async () => {
      const [reasons, algorithm] = await Promise.all([
        getForecastReasons(apiClient),
        isFeatureEnabled(apiClient, Feature.NewAlgorithm, store.storeId),
      ])

      setIsNewAlgorithm(algorithm)
      if (algorithm) {
        setForecastReasons([...reasons])
      }
    }

    initialLoad()
  }, [])

  useEffect(() => {
    const el = document.getElementById('mix-scroll-area')
    if (el) {
      setTimeout(() => {
        el.scrollTop = currentMixScrollPosition
      }, 10)
    }
  }, [currentMixScrollPosition])

  useEffect(() => {
    const loadMixSkusData = async () => {
      setIsLoading(true)
      resetError() // Clear previous errors

      try {
        const [mixSkus, isStoreZeroErrorsEnabled] = await Promise.all([
          getMixSkus(apiClient, store.storeId, planDate, currentMixId),
          isFeatureEnabled(apiClient, Feature.StoreZeroErrors, 0),
        ])

        const initMix = mixSkus[0]

        setMix(initMix)
        setAllMixSkusMarked(
          initMix.mixSkus.some((mixSku) => mixSku.marked === false)
            ? false
            : true,
        )

        if (isStoreZeroErrorsEnabled) {
          const storeError = checkStore(store)
          if (storeError) {
            handleError(storeError)
          }
        }
      } catch (e) {
        handleError(ErrorMessage.ProductionView.FailedToLoad, e as Error)
      } finally {
        setIsLoading(false)
      }
    }

    loadMixSkusData()
  }, [apiClient, store, planDate, currentMixId, handleError, resetError])

  return (
    <>
      {isLoading && <ProgressLoading message='Loading...' />}

      <Container>
        <Card
          orientation='vertical'
          className='bakery-container-pad bakery-card c-mix-wrapper'
          style={{
            maxWidth: 1500,
            margin: '40px auto',
            boxSizing: 'border-box',
            padding: '16px 18px',
            position: 'relative',
          }}
        >
          <Button
            title='Close'
            aria-label='Close mix view'
            type='button'
            onClick={handleUpdateCloseView}
            icon={<Close />}
            isFullWidth={false}
            variant='tertiary'
            size='sm'
            className='c-mix-close-btn'
            testid='mix-cancel-button'
          />

          {!isLoading && errorMessage && (
            <ErrorRefresh message={errorMessage} />
          )}

          {!isLoading &&
            !errorMessage &&
            mix !== undefined &&
            mix.plannerGroup !== undefined && (
              <>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: '1rem',
                  }}
                >
                  <MixSkusTitle
                    plannerGroupName={
                      mix.plannerGroup !== null
                        ? mix.plannerGroup.plannerGroupName
                        : ''
                    }
                    ovenSettingDetail={mix.ovenSettingDetail}
                  />
                </div>

                <div
                  id='mix-scroll-area'
                  style={{
                    height: 'calc(100vh - 220px)',
                    minHeight: '400px',
                    overflow: 'auto',
                    WebkitOverflowScrolling: 'touch',
                    marginBottom: '16px',
                  }}
                >
                  <Table
                    className='c-mix-skus-table'
                    isResponsive={true}
                    respondAt='sm'
                    isStriped
                    layout='auto'
                    testid='mix-skus-table'
                  >
                    <MixSkusTableHeader
                      mix={mix}
                      allMixSkusMarked={allMixSkusMarked}
                      handleCheckAll={handleAllSkusChecked}
                      isNewAlgorithm={isNewAlgorithm}
                    />
                    <TableBody>{renderSkuList(mix)}</TableBody>
                  </Table>
                  {canProgressMix() === false && (
                    <div className='c-mix-audit' data-testid='mix-audit'>
                      <Body1>
                        Started by: {mix.startedBy} at{' '}
                        {toUkTimeDateStringFrom(mix.startedAt)}
                      </Body1>
                      <br />
                      <Body1>
                        Completed by: {mix.completedBy} at{' '}
                        {toUkTimeDateStringFrom(mix.completedAt)}
                      </Body1>
                    </div>
                  )}
                </div>
                <div>
                  {mix !== undefined && (
                    <Card orientation='vertical'>
                      {params.preview !== 'true' &&
                        canProgressMix() === true &&
                        mix.mixStatusStageId ===
                          (MixStageEnum.Pending as number) && (
                          <ProductionPlanningButton
                            isFullWidth
                            handler={() => progressMix()}
                            testid='mix-start-button'
                          >
                            Start
                          </ProductionPlanningButton>
                        )}

                      {params.preview === 'true' && (
                        <Button
                          disabled={mix.updating === true}
                          isFullWidth
                          onClick={handleUpdateCloseView}
                          testid='mix-close-preview-button'
                        >
                          Close preview
                        </Button>
                      )}

                      {canProgressMix() === true &&
                        mix.mixStatusStageId ===
                          (MixStageEnum.InProgress as number) && (
                          <Grid cols={2} colSize='auto' gap='lg'>
                            <GridItem>
                              <Button
                                disabled={mix.updating === true}
                                onClick={handleUpdateCloseView}
                                variant='secondary'
                                style={{ backgroundColor: 'white' }}
                                isFullWidth
                                testid='mix-save-close-button'
                              >
                                Save and close
                              </Button>
                            </GridItem>
                            <GridItem>
                              <ProductionPlanningButton
                                handler={() => progressMix()}
                                testid='mix-complete-button'
                                isFullWidth
                              >
                                All items completed
                              </ProductionPlanningButton>
                            </GridItem>
                          </Grid>
                        )}

                      {canProgressMix() === false && renderEditButton(mix)}
                    </Card>
                  )}
                </div>
              </>
            )}
        </Card>
      </Container>
    </>
  )
}

export default MixSkusView
